import { doc, getDoc, setDoc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { APP_CONFIG } from '../../constants/config';

interface LoginAttempt {
  count: number;
  lastAttempt: Date | null;
  blocked: boolean;
  email: string;
}

/**
 * Vérifie si un utilisateur est bloqué en raison de trop nombreuses tentatives de connexion
 */
export const isUserBlocked = async (email: string): Promise<boolean> => {
  if (!email) return false;
  
  try {
    const loginAttemptsRef = doc(db, 'loginAttempts', email);
    const loginAttemptsDoc = await getDoc(loginAttemptsRef);
    
    if (!loginAttemptsDoc.exists()) {
      return false;
    }
    
    const data = loginAttemptsDoc.data();
    
    // Si l'utilisateur est marqué comme bloqué
    if (data.blocked) {
      // Vérifier si la période de blocage est terminée
      const lastAttempt = data.lastAttempt?.toDate();
      if (lastAttempt) {
        const blockEndTime = new Date(lastAttempt.getTime() + APP_CONFIG.LOGIN_TIMEOUT);
        const now = new Date();
        
        // Si la période de blocage est terminée, débloquer l'utilisateur
        if (now > blockEndTime) {
          await updateDoc(loginAttemptsRef, {
            blocked: false,
            count: 0
          });
          return false;
        }
        
        return true;
      }
    }
    
    return false;
  } catch (error) {
    console.error('Error checking if user is blocked:', error);
    return false;
  }
};

/**
 * Récupère le temps restant avant déblocage (en secondes)
 */
export const getRemainingBlockTime = async (email: string): Promise<number> => {
  if (!email) return 0;
  
  try {
    const loginAttemptsRef = doc(db, 'loginAttempts', email);
    const loginAttemptsDoc = await getDoc(loginAttemptsRef);
    
    if (!loginAttemptsDoc.exists() || !loginAttemptsDoc.data().blocked) {
      return 0;
    }
    
    const data = loginAttemptsDoc.data();
    const lastAttempt = data.lastAttempt?.toDate();
    
    if (!lastAttempt) {
      return 0;
    }
    
    const blockEndTime = new Date(lastAttempt.getTime() + APP_CONFIG.LOGIN_TIMEOUT);
    const now = new Date();
    
    if (now > blockEndTime) {
      return 0;
    }
    
    return Math.ceil((blockEndTime.getTime() - now.getTime()) / 1000);
  } catch (error) {
    console.error('Error getting remaining block time:', error);
    return 0;
  }
};

/**
 * Enregistre une tentative de connexion échouée
 */
export const recordFailedLoginAttempt = async (email: string): Promise<void> => {
  if (!email) return;
  
  try {
    const loginAttemptsRef = doc(db, 'loginAttempts', email);
    const loginAttemptsDoc = await getDoc(loginAttemptsRef);
    
    if (!loginAttemptsDoc.exists()) {
      // Créer un nouveau document pour cet email
      await setDoc(loginAttemptsRef, {
        email,
        count: 1,
        lastAttempt: serverTimestamp(),
        blocked: false
      });
      return;
    }
    
    const data = loginAttemptsDoc.data();
    const newCount = data.count + 1;
    const updates: any = {
      count: newCount,
      lastAttempt: serverTimestamp()
    };
    
    // Bloquer l'utilisateur si le nombre de tentatives dépasse le maximum
    if (newCount >= APP_CONFIG.MAX_LOGIN_ATTEMPTS) {
      updates.blocked = true;
    }
    
    await updateDoc(loginAttemptsRef, updates);
  } catch (error) {
    console.error('Error recording failed login attempt:', error);
  }
};

/**
 * Réinitialise les tentatives de connexion après une connexion réussie
 */
export const resetLoginAttempts = async (email: string): Promise<void> => {
  if (!email) return;
  
  try {
    const loginAttemptsRef = doc(db, 'loginAttempts', email);
    const loginAttemptsDoc = await getDoc(loginAttemptsRef);
    
    if (loginAttemptsDoc.exists()) {
      await updateDoc(loginAttemptsRef, {
        count: 0,
        blocked: false,
        lastAttempt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error resetting login attempts:', error);
  }
};

/**
 * Enregistre une tentative de réinitialisation de mot de passe
 * Note: Cette fonction ne nécessite pas d'authentification dans les règles Firestore
 */
export const recordPasswordResetAttempt = async (email: string): Promise<void> => {
  if (!email) return;
  
  try {
    const loginAttemptsRef = doc(db, 'loginAttempts', email);
    
    // Vérifier si le document existe déjà
    const loginAttemptsDoc = await getDoc(loginAttemptsRef);
    
    if (!loginAttemptsDoc.exists()) {
      // Créer un nouveau document pour cet email
      await setDoc(loginAttemptsRef, {
        email,
        count: 0, // Ne pas incrémenter le compteur pour les réinitialisations de mot de passe
        lastAttempt: serverTimestamp(),
        blocked: false,
        passwordResetAttempts: 1,
        lastPasswordResetAttempt: serverTimestamp()
      });
    } else {
      // Mettre à jour le document existant
      const data = loginAttemptsDoc.data();
      const passwordResetAttempts = (data.passwordResetAttempts || 0) + 1;
      
      await updateDoc(loginAttemptsRef, {
        passwordResetAttempts,
        lastPasswordResetAttempt: serverTimestamp()
      });
    }
  } catch (error) {
    console.error('Error recording password reset attempt:', error);
    throw error; // Propager l'erreur pour la gérer dans le composant
  }
};